import type { Citation } from "./citations";

/**
 * Format a citation as an academic-style plain-text reference line.
 * Simplified for paperclip's paper-only citation model.
 */
export const formatCitationReference = (c: Citation): string => {
  const isBioRxiv = c.source === "biorxiv" || c.source_type === "biorxiv_xml";
  const isMedRxiv = c.source === "medrxiv" || c.source_type === "medrxiv_xml";
  const isPmc = c.source === "pmc" || c.source_type === "pmc_xml";

  if (c.doi || isBioRxiv || isMedRxiv || isPmc) {
    const parts: string[] = [];
    if (c.authors) parts.push(c.authors + ".");
    if (c.doc_title) {
      parts.push(c.doc_title + ".");
    } else if (c.content) {
      const excerpt = c.content.trim().slice(0, 160);
      parts.push(`"${excerpt}${c.content.length > 160 ? "…" : ""}"`);
    }
    if (isPmc) {
      parts.push("PubMed Central.");
    } else {
      const server = isMedRxiv ? "medRxiv" : "bioRxiv";
      parts.push(`${server} [Preprint].`);
    }
    if (c.month_year) parts.push(c.month_year.replace(/_/g, " ") + ".");
    if (c.doi) parts.push(`https://doi.org/${c.doi}`);
    else if (c.public_url) parts.push(c.public_url);
    return parts.join(" ");
  }

  if (c.doc_title) {
    const parts: string[] = [];
    parts.push(c.doc_title + ".");
    if (c.public_url) parts.push(c.public_url);
    else if (c.doi) parts.push(`https://doi.org/${c.doi}`);
    return parts.join(" ");
  }

  const isUuid =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      c.doc_id ?? "",
    );
  const link = c.public_url || (c.doi ? `https://doi.org/${c.doi}` : "");
  const docRef = !isUuid ? c.doc_id : "";

  if (!link && !docRef) return "";

  const excerpt = c.content?.trim().slice(0, 120);
  const excerptStr = excerpt
    ? `"${excerpt}${(c.content?.length ?? 0) > 120 ? "…" : ""}"`
    : "";
  return [excerptStr, link || docRef].filter(Boolean).join(" ");
};

/** Format sorted citation numbers as compact ranges, e.g. [1,2,3,5] -> "[1-3,5]". */
export const formatCitationNumbers = (ids: number[]): string => {
  const sorted = [...ids].sort((a, b) => a - b);
  const ranges: string[] = [];
  let start = sorted[0];
  let end = sorted[0];
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i] === end + 1) {
      end = sorted[i];
    } else {
      ranges.push(start === end ? `${start}` : `${start}–${end}`);
      start = sorted[i];
      end = sorted[i];
    }
  }
  ranges.push(start === end ? `${start}` : `${start}–${end}`);
  return `[${ranges.join(",")}]`;
};

/**
 * Replace inline `[N]` markers in text, then append a numbered reference list.
 * The text already contains `[N]` markers from the backend; this just appends
 * the bibliographic reference block at the bottom.
 */
export const formatContentWithReferences = (
  text: string,
  globalCitations: Citation[],
): string => {
  const usedIds = new Set<number>();
  const markerRegex = /\[(\d+)\]/g;
  let m: RegExpExecArray | null;
  while ((m = markerRegex.exec(text)) !== null) {
    usedIds.add(Number(m[1]));
  }

  const referencedCitations = globalCitations
    .filter((c) => usedIds.has(c.id))
    .sort((a, b) => a.id - b.id);

  if (referencedCitations.length === 0) return text.trim();

  const docGroups = new Map<string, { ids: number[]; citation: Citation }>();
  for (const c of referencedCitations) {
    const key = c.doc_id || String(c.id);
    if (!docGroups.has(key)) {
      docGroups.set(key, { ids: [], citation: c });
    }
    docGroups.get(key)!.ids.push(c.id);
  }

  const groups = Array.from(docGroups.values()).sort(
    (a, b) => a.ids[0] - b.ids[0],
  );

  const refLines = groups
    .map(({ ids, citation }) => {
      const ref = formatCitationReference(citation);
      if (!ref) return "";
      return `${formatCitationNumbers(ids)} ${ref}`;
    })
    .filter(Boolean)
    .join("\n");

  return `${text.trim()}\n\n---\nReferences\n\n${refLines}`;
};

const ENRICH_TIMEOUT_MS = 4000;

/**
 * Enrich citations missing metadata (authors, title) via a fetch function.
 * Best-effort: returns unenriched citations if the timeout is exceeded.
 */
export const enrichCitations = async (
  citations: Citation[],
  fetchMetadata: (c: Citation) => Promise<Partial<Citation> | null>,
): Promise<Citation[]> => {
  const enriched = Promise.all(
    citations.map(async (c) => {
      if (!c.authors && !c.doc_title) {
        const meta = await fetchMetadata(c);
        if (meta) return { ...c, ...meta };
      }
      return c;
    }),
  );

  const timeout = new Promise<Citation[]>((resolve) =>
    setTimeout(() => resolve(citations), ENRICH_TIMEOUT_MS),
  );

  return Promise.race([enriched, timeout]);
};
